import * as React from 'react';
import { useTheme } from '@veneer/theme';
import propTypes from './prop_types';
import useControlled from '../utils/use_controlled';
import mergeRefs from 'react-merge-refs';
import useStyles from './use_styles';
import { TooltipInterface } from './types';

import highContrastMediaQuery from '../utils/high_contrast_media_query';
import { usePlacement } from '../shared/floating';
import FloatingArrow from '../shared/floating_arrow/floating_arrow';
import {
  useFloating,
  useHover,
  useInteractions,
  arrow,
  flip,
  offset,
  safePolygon,
  useId,
} from '@floating-ui/react';
import Portal from '../shared/portal';
import primitives from '@veneer/primitives';
import useClosePopoverAfterScrollOrZoom from '../utils/use_close_popover_after_scroll_or_zoom';

const Tooltip = React.forwardRef<Element, TooltipInterface>(
  (
    {
      'aria-hidden': ariaHidden,
      arrow: hasArrow = false,
      children,
      content,
      contentHideDelayOnHover = 300,
      contentShowDelayOnHover = 0,
      disabled = false,
      forTruncation = false,
      id: idProp,
      maxWidth,
      open: openProp,
      onBlur,
      onFocus,
      placement: placementProp = 'bottom',
      portal = false,
      tabIndex,
      useJsPositioning = false,
      userSelect = 'none',
      childRef,
    },
    ref
  ) => {
    const theme = useTheme();
    const isHc = theme.mode === 'contrast';

    const autoId = useId();
    const id = idProp ?? autoId;

    const [open, setOpen] = useControlled<boolean>({
      controlled: openProp,
      default: false,
    });

    const handleFocus = React.useCallback<React.FocusEventHandler>(
      (event) => {
        setOpen(!disabled);
        onFocus?.(event);
      },
      [onFocus, setOpen, disabled]
    );

    const handleBlur = React.useCallback<React.FocusEventHandler>(
      (event) => {
        setOpen(false);
        onBlur?.(event);
      },
      [onBlur, setOpen]
    );

    const arrowRef = React.useRef(null);
    const childrenRef = React.useRef<HTMLElement>(null);
    const placement = usePlacement(placementProp);

    useClosePopoverAfterScrollOrZoom(open, setOpen);

    const { refs, floatingStyles, context } = useFloating({
      open,
      onOpenChange: setOpen,
      placement,
      middleware: [
        offset(hasArrow ? primitives.layout.size3 : primitives.layout.size1),
        hasArrow &&
          arrow({ element: arrowRef, padding: primitives.layout.size2 + 2 }),
        useJsPositioning && flip(),
      ],
    });

    const hover = useHover(context, {
      enabled: !disabled,
      handleClose: safePolygon(),
      delay: {
        open: contentShowDelayOnHover,
        close: contentHideDelayOnHover,
      },
    });

    const { getReferenceProps, getFloatingProps } = useInteractions([hover]);

    const handleRef = childRef
      ? mergeRefs([childRef, refs.setReference])
      : refs.setReference;

    const childrenProps: React.HTMLAttributes<HTMLElement> &
      React.RefAttributes<HTMLElement> = {
      ref: mergeRefs([childrenRef, handleRef]),
      ...(!forTruncation && {
        ...(open && { 'aria-describedby': id }),
        onBlur: handleBlur,
        onFocus: handleFocus,
        tabIndex: 0,
      }),
      ...getReferenceProps(),
    };

    const style = useStyles({ maxWidth, userSelect });

    React.useEffect(() => {
      const handleKeyDown = (event: KeyboardEvent) => {
        if (event.key === 'Escape') setOpen(false);
      };
      if (open) document.addEventListener('keydown', handleKeyDown);
      else document.removeEventListener('keydown', handleKeyDown);

      function handleFocusIn(event: FocusEvent) {
        const target = event.target as HTMLElement;

        if (
          !open &&
          childrenRef.current?.parentElement?.contains(target) &&
          forTruncation
        ) {
          setOpen(true);
        }
      }

      function handleFocusOut() {
        if (open) setOpen(false);
      }

      document.addEventListener('focusin', handleFocusIn);
      document.addEventListener('focusout', handleFocusOut);

      return () => {
        document.removeEventListener('keydown', handleKeyDown);
        document.removeEventListener('focusin', handleFocusIn);
        document.removeEventListener('focusout', handleFocusOut);
      };
    }, [forTruncation, open, setOpen]);

    return (
      <>
        {React.cloneElement(children, childrenProps)}
        {open && (
          <Portal disablePortal={!portal}>
            <div
              aria-hidden={(ariaHidden ?? forTruncation) ? true : undefined}
              tabIndex={tabIndex ?? undefined}
              css={[style.root, highContrastMediaQuery(theme)]}
              id={id}
              ref={mergeRefs([ref, refs.setFloating])}
              role="tooltip"
              style={{
                ...floatingStyles,
                zIndex: theme.elevation.tooltip,
              }}
              {...getFloatingProps()}
            >
              <div css={[style.tooltip.root, isHc && style.tooltip.hc]}>
                {content}
                {hasArrow && <FloatingArrow context={context} ref={arrowRef} />}
              </div>
            </div>
          </Portal>
        )}
      </>
    );
  }
);

Tooltip.propTypes = propTypes;

Tooltip.displayName = 'Tooltip';

export default Tooltip;
